"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Welcome from "./WelcomeStep";
import { useProviderUser } from "@/hooks/useProviderUser";
import OnboardingStepper from "./OnboardingStepper";
import { useNavigate } from "@tanstack/react-router";
import {
  getAllOnboardingSteps,
  getOnboardingSteps,
} from "../utils/getOnboardingSteps";
import { ProviderOnboardingStep } from "../enums/ProviderOnboardingStep";
import { useTRPC } from "@/trpc";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const OnboardingContainer = () => {
  const [step, setStep] = useState<number>(0);
  const [initialized, setInitialized] = useState(false);
  const queryClient = useQueryClient();
  const providerUser = useProviderUser();
  const trpc = useTRPC();
  const completeRosterOnboarding = useMutation(
    trpc.providerOnboarding.completeRosterOnboarding.mutationOptions(),
  );
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  // Get ALL steps for navigation (not just incomplete ones)
  const allSteps = useMemo(
    () => getAllOnboardingSteps(providerUser),
    [providerUser],
  );

  // Initialize step to first incomplete step on mount
  useEffect(() => {
    if (!providerUser || initialized) {
      return;
    }

    const incompleteSteps = getOnboardingSteps(providerUser);

    if (incompleteSteps.length === 0) {
      // All steps complete, redirect to dashboard
      navigate({ to: "/provider/home", resetScroll: false });
      return;
    }

    // Find the index of the first incomplete step in ALL steps
    const firstIncompleteStep = allSteps.findIndex(
      (s) => s.id === incompleteSteps[0].id,
    );

    if (firstIncompleteStep !== -1) {
      setStep(firstIncompleteStep);
    }
    setInitialized(true);
  }, [providerUser?.id, allSteps, initialized, navigate]);

  const onComplete = async () => {
    if (step == null) {
      return;
    }

    // Invalidate the cache and fetch fresh data
    await queryClient.invalidateQueries({
      queryKey: trpc.provider.getProviderUser.queryKey(),
    });

    // Fetch fresh provider user data to check completion status
    const freshProviderUser = await queryClient.fetchQuery(
      trpc.provider.getProviderUser.queryOptions(),
    );

    // Check if onboarding is complete using the fresh data
    const isOnboardingComplete =
      getOnboardingSteps(freshProviderUser).length === 0;

    if (isOnboardingComplete) {
      // No-op for ordinary/existing providers. For an account created by a
      // roster invitation, this advances Started → Onboarded and stamps the
      // corresponding affiliation completion timestamp.
      await completeRosterOnboarding.mutateAsync();
      // All steps complete, redirect to the provider dashboard
      navigate({
        to: "/provider/home",
        resetScroll: false,
      });
      return;
    }

    setStep((currentStep) => currentStep + 1);
  };

  // Navigate to previous step
  const onBack = () => {
    if (step > 0) {
      setStep((currentStep) => currentStep - 1);
    }
  };

  // Navigate directly to a step (used by stepper clicks)
  const onNavigateToStep = (stepId: ProviderOnboardingStep) => {
    const targetIndex = allSteps.findIndex((s) => s.id === stepId);
    if (targetIndex !== -1) {
      setStep(targetIndex);
    }
  };

  const stepData = allSteps[step];

  const Component = step == null ? step : stepData?.component;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "auto", block: "start" });
    }

    window.scrollTo({ top: 0, behavior: "auto" });
  }, [providerUser, step]);

  if (!providerUser) {
    return null;
  }

  if (Component === Welcome) {
    return <Welcome onComplete={onComplete} />;
  }

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 sm:py-8 lg:px-8"
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-5 lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-8">
        <div className="min-w-0">
          <div
            className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-xs lg:px-5 lg:py-7 ${stepData?.hideStepper ? "hidden lg:block" : ""}`}
          >
            <OnboardingStepper
              onboardingSteps={allSteps}
              stepIndex={step}
              providerUser={providerUser}
              onNavigateToStep={onNavigateToStep}
            />
          </div>
        </div>
        <div className="min-w-0">
          <div
            className={`w-full rounded-2xl border border-slate-200 bg-white px-5 shadow-xs ${stepData?.hideStepper ? "py-12" : "py-6"} sm:p-8`}
          >
            {Component != null && (
              <Component
                providerUser={providerUser}
                onComplete={onComplete}
                onBack={step > 0 ? onBack : undefined}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingContainer;
